"use client";

import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import { PerksSection } from "@/features/home/ui/components/PerksSection";
import { RouteLoadingLinkStatus } from "@/features/navigation/ui/components/RouteLoadingSpinner";
import {
  getPriceCollectionCurrencySymbol,
  normalizeCurrencyCode,
} from "@/features/products/shared/priceCollections";
import { isNewArrivalLabels, NEW_ARRIVAL_PRODUCT_SORT } from "@/features/products/shared/newArrivalSort";
import type { ProductGridFilterLink } from "@/features/products/shared/productGridFilterLinks";
import { Link } from "@/i18n/navigation";
import { NotFound } from "@/lib/client/components/NotFound";
import { useCountryStore } from "@/lib/client/stores/useCountryStore";
import { normalizePathname } from "@/lib/shared/seo";
import { getBreadcrumbHref, type IBreadcrumbsNav } from "@/lib/shared/types/breadcrumbs";
import type { ProductForClient } from "@/lib/shared/types/interfaces";
import type { IPaginatedResult, ISortCriteria } from "@/lib/shared/types/pagination";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Container,
  cn,
  LoadingOverlay,
} from "@/ui";
import { useProducts } from "../hooks/useProducts";
import { Pagination } from "./Pagination";
import type { ProductGridPriceFilter } from "./ProductGridHeader";
import { ProductsGrid } from "./ProductsGrid";
import { Sort } from "./Sort";

const URL_SORT_CRITERIA = new Set(["priority", "displayPrice", "updatedAt", "createdAt"]);

const DEFAULT_PRODUCT_SORT: ISortCriteria = { criteria: "priority", order: "asc" };

interface ProductGridContainerProps {
  products: IPaginatedResult<ProductForClient>;
  title?: string;
  description?: string;
  seoLinks?: ProductGridFilterLink[];
  labels: string[];
  breadcrumbsNav: IBreadcrumbsNav;
  priceFilter?: ProductGridPriceFilter & { initialCurrencyCode: string };
}

export const ProductGridContainer = (props: ProductGridContainerProps) => {
  const { products, title, seoLinks, labels, breadcrumbsNav, priceFilter } = props;
  const defaultSort = isNewArrivalLabels(labels) ? NEW_ARRIVAL_PRODUCT_SORT : DEFAULT_PRODUCT_SORT;
  const navigationT = useTranslations("navigation");
  const collectionT = useTranslations("collection_landing");
  const { countries, country } = useCountryStore();
  const [sortCriteria, setSortCriteria] = useState<ISortCriteria>(defaultSort);
  const [page, setPage] = useState(1);
  const [pathname, setPathname] = useState("");

  const pageChangeRequestedRef = useRef(false);
  const level1Href = getBreadcrumbHref(breadcrumbsNav, "level1");
  const level2Href = getBreadcrumbHref(breadcrumbsNav, "level2");
  const level3Href = getBreadcrumbHref(breadcrumbsNav, "level3");
  const selectedCurrencyCode = normalizeCurrencyCode(country.currencyCode);
  const selectedCurrencyIsSupported =
    priceFilter?.supportedCurrencyCodes.some(
      (currencyCode) => normalizeCurrencyCode(currencyCode) === selectedCurrencyCode,
    ) ?? false;
  const priceFilterCurrencyIsSupported = Boolean(priceFilter) && selectedCurrencyIsSupported;
  const activePriceFilter =
    priceFilter && priceFilterCurrencyIsSupported
      ? {
          countryCode: country.countryCode,
          currencyCode: selectedCurrencyCode,
          maxPrice: priceFilter.maxPrice,
        }
      : undefined;
  const priceFilterIsWaiting = Boolean(priceFilter && countries == null);
  const priceFilterIsUnavailable = Boolean(
    priceFilter && countries != null && !priceFilterCurrencyIsSupported,
  );
  const priceVars = {
    amount: priceFilter?.maxPrice ?? 80,
    symbol: getPriceCollectionCurrencySymbol(selectedCurrencyCode),
  };
  const resolvedTitle =
    priceFilter && priceFilterCurrencyIsSupported
      ? collectionT("jewellery_under_price.title", priceVars)
      : (title ?? navigationT(breadcrumbsNav.current));

  const { data: queryData, isFetching } = useProducts({
    labels: priceFilter ? [] : labels,
    page,
    sort: sortCriteria,
    enabled: priceFilter ? Boolean(activePriceFilter) : true,
    initialData: products,
    initialSort: defaultSort,
    initialDataCurrencyCode: priceFilter?.initialCurrencyCode,
    priceFilter: activePriceFilter,
  });

  const canUseInitialProducts =
    !priceFilter ||
    activePriceFilter?.currencyCode === normalizeCurrencyCode(priceFilter.initialCurrencyCode);
  const productsData = queryData ?? (canUseInitialProducts ? products : undefined);
  const showingCount =
    productsData?.docs?.filter((p): p is ProductForClient => p != null).length ?? 0;
  const totalCount = productsData?.totalDocs ?? 0;
  const normalizedPathname = normalizePathname(pathname);
  const parentCategory = [breadcrumbsNav.level2, breadcrumbsNav.current].find(
    (value) => value === "new" || value === "sales",
  );

  const visibleSeoLinks = seoLinks?.filter((link) => {
    if (!link.supportedCurrencyCodes?.length) return true;
    return link.supportedCurrencyCodes.some(
      (currencyCode) => normalizeCurrencyCode(currencyCode) === selectedCurrencyCode,
    );
  });

  const resolvedSeoLinks =
    parentCategory && visibleSeoLinks?.length
      ? visibleSeoLinks.map((link) => {
          const subcategoryMatch = link.href.match(/^\/products\/jewellery\/([^/]+)$/);
          if (!subcategoryMatch) {
            return link;
          }

          return {
            ...link,
            href: `/products/categories/${parentCategory}/${subcategoryMatch[1]}`,
          };
        })
      : visibleSeoLinks;

  useEffect(() => {
    setPathname(window.location.pathname);

    // Load initial values from searchParams if they exist
    const currentParams = new URLSearchParams(window.location.search);
    const initialSortCriteria = currentParams.get("sortCriteria");
    const initialSortOrder = currentParams.get("sortOrder");
    const initialPage = currentParams.get("page");
    if (
      initialSortCriteria &&
      initialSortOrder &&
      URL_SORT_CRITERIA.has(initialSortCriteria) &&
      (initialSortOrder === "asc" || initialSortOrder === "desc")
    ) {
      setSortCriteria({
        criteria: initialSortCriteria as ISortCriteria["criteria"],
        order: initialSortOrder,
      });
    }
    if (initialPage) {
      setPage(Number.parseInt(initialPage));
    }
  }, []);

  const onSortCriteriaChange = (nextSort: ISortCriteria) => {
    setSortCriteria(nextSort);
    setPage(1);
    const currentParams = new URLSearchParams(window.location.search);
    currentParams.set("sortCriteria", nextSort.criteria);
    currentParams.set("sortOrder", nextSort.order);
    currentParams.delete("page");
    const query = currentParams.toString();
    window.history.replaceState(
      null,
      "",
      query ? `${window.location.pathname}?${query}` : window.location.pathname,
    );
  };

  const onPageChange = (page: number) => {
    pageChangeRequestedRef.current = true;
    setPage(page);
    const currentParams = new URLSearchParams(window.location.search);
    currentParams.set("page", String(page));
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}?${currentParams.toString()}`,
    );
  };

  useEffect(() => {
    if (pageChangeRequestedRef.current && !isFetching && productsData?.docs) {
      pageChangeRequestedRef.current = false;
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [isFetching, productsData?.docs]);

  if (priceFilterIsUnavailable) {
    return <NotFound />;
  }

  const isWaitingForProducts = Boolean(
    priceFilter && priceFilterCurrencyIsSupported && !productsData?.docs,
  );

  return productsData?.docs || priceFilterIsWaiting || isWaitingForProducts ? (
    <>
      <LoadingOverlay isLoading={isFetching || priceFilterIsWaiting || isWaitingForProducts} />
      <Container>
        {resolvedSeoLinks?.length ? (
          <div className="mb-6 flex flex-wrap gap-2">
            {resolvedSeoLinks.map((link) => {
              const isActive = normalizedPathname === normalizePathname(link.href);
              return (
                <Link
                  key={link.href}
                  href={link.href}
                  aria-current={isActive ? "page" : undefined}
                  className={cn(
                    "rounded-full border px-3 py-1 text-sm transition-colors",
                    isActive
                      ? "border-primary bg-primary font-medium text-primary-foreground hover:bg-primary/90"
                      : "border-border/60 text-foreground hover:border-primary hover:text-primary",
                  )}
                >
                  {link.priceAmount
                    ? navigationT(link.label, {
                        amount: link.priceAmount,
                        symbol: getPriceCollectionCurrencySymbol(selectedCurrencyCode),
                      })
                    : navigationT(link.label)}
                  <RouteLoadingLinkStatus />
                </Link>
              );
            })}
          </div>
        ) : null}
        <div className="mb-2 flex flex-col gap-4 sm:mb-0 sm:flex-row sm:items-center sm:justify-between">
          <Breadcrumb className="min-w-0 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] sm:max-w-[calc(100%-12rem)] [&::-webkit-scrollbar]:hidden">
            <BreadcrumbList className="flex-nowrap">
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link href="/">
                    {navigationT("home")}
                    <RouteLoadingLinkStatus />
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              {breadcrumbsNav.level1 && (
                <>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    {level1Href ? (
                      <BreadcrumbLink asChild>
                        <Link href={level1Href}>
                          {navigationT(breadcrumbsNav.level1)}
                          <RouteLoadingLinkStatus />
                        </Link>
                      </BreadcrumbLink>
                    ) : (
                      <BreadcrumbPage>{navigationT(breadcrumbsNav.level1)}</BreadcrumbPage>
                    )}
                  </BreadcrumbItem>
                </>
              )}
              {breadcrumbsNav.level2 && (
                <>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    {level2Href ? (
                      <BreadcrumbLink asChild>
                        <Link href={level2Href}>
                          {navigationT(breadcrumbsNav.level2)}
                          <RouteLoadingLinkStatus />
                        </Link>
                      </BreadcrumbLink>
                    ) : (
                      <BreadcrumbPage>{navigationT(breadcrumbsNav.level2)}</BreadcrumbPage>
                    )}
                  </BreadcrumbItem>
                </>
              )}
              {breadcrumbsNav.level3 && (
                <>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    {level3Href ? (
                      <BreadcrumbLink asChild>
                        <Link href={level3Href}>
                          {navigationT(breadcrumbsNav.level3)}
                          <RouteLoadingLinkStatus />
                        </Link>
                      </BreadcrumbLink>
                    ) : (
                      <BreadcrumbPage>{navigationT(breadcrumbsNav.level3)}</BreadcrumbPage>
                    )}
                  </BreadcrumbItem>
                </>
              )}
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{resolvedTitle}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          <div className="flex w-full shrink-0 items-center justify-between gap-3 sm:w-auto sm:justify-end">
            <span className="text-xs text-muted-foreground sm:text-sm whitespace-nowrap">
              {navigationT("showing_count", { showingCount, totalCount })}
            </span>
            <Sort
              sortCriteria={sortCriteria}
              onSortCriteriaChange={onSortCriteriaChange}
            />
          </div>
        </div>
        <ProductsGrid
          products={productsData?.docs?.filter((p): p is ProductForClient => p != null) ?? []}
        />
        {productsData?.docs ? (
          <Pagination
            pagination={{
              ...productsData,
            }}
            onPageChange={onPageChange}
          />
        ) : null}
        <PerksSection />
      </Container>
    </>
  ) : (
    <NotFound />
  );
};
